import { get } from 'lodash';
import React, { Suspense, lazy } from 'react';
import { Provider, connect } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import styled from 'styled-components';

import { BugsDropdown, Loader } from '@components';
import ThemeProvider from '../themes/internal';

import store, { history } from '../store';
import GlobalStyles from '../styles';
import { B2BAuthGate } from '../modules/b2b';

import Header from './Header';
import Pages from './Pages';

const Dialer = lazy(() => import('./Dialer.jsx'));

interface AppProps {
  errors: Array<{
    createdAt: number;
    message: string;
    title: string;
  }>;
  isCurrentManagerExist: boolean;
  isAgentMode: boolean;
  isOperator: boolean;
  theme: 'light' | 'dark';
}

class App extends React.PureComponent<AppProps> {
  render() {
    const { errors, isAgentMode, isCurrentManagerExist, isOperator, theme } =
      this.props;

    if (!isCurrentManagerExist) {
      return (
        <StyledContainer>
          <GlobalStyles theme={theme} />
          <StyledLoaderWrapper>
            <Loader />
            <StyledBugsDropdown list={errors} onToggle={() => null} />
          </StyledLoaderWrapper>
        </StyledContainer>
      );
    }

    return (
      <>
        <GlobalStyles theme={theme} />
        <ThemeProvider>
          <StyledContainer>
            <StyledHeader />
            {!isAgentMode && isOperator && (
              <Suspense fallback={<div />}>
                <StyledDialer />
              </Suspense>
            )}
            <StyledPages />
          </StyledContainer>
        </ThemeProvider>
      </>
    );
  }
}

const mapStateToProps = ({ appMode, general }) => {
  const isCurrentManagerExist = !!get(general, 'currentManager.id');
  const isOperator = get(general, 'currentManager.isOperator');
  const errors = get(general, 'errors');
  const theme = get(general, 'theme');

  return {
    errors,
    isAgentMode: appMode.isAgentMode,
    isCurrentManagerExist,
    isOperator,
    theme,
  };
};

const ConnectedApp = connect(mapStateToProps)(App);

const AppWithStore = () => (
  <Provider store={store}>
    <ConnectedRouter history={history}>
      <B2BAuthGate>
        <ConnectedApp />
      </B2BAuthGate>
    </ConnectedRouter>
  </Provider>
);

const StyledContainer = styled.div`
  background-color: #e6e6e6;
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-width: 1024px;
  width: 100vw;
`;

const StyledHeader = styled(Header)`
  flex-shrink: 0;
`;

const StyledDialer = styled(Dialer)`
  flex-shrink: 0;
  min-width: initial;
`;

const StyledPages = styled(Pages)`
  height: 100%;
  overflow: hidden;
`;

const StyledLoaderWrapper = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const StyledBugsDropdown = styled(BugsDropdown)`
  border: none;
  background: none;
`;

export default AppWithStore;
